/**
 * PanViewContext — which PAN the customer is currently working as.
 *
 * The choice is a UI preference, so it lives in localStorage rather than on
 * the server: every PAN-scoped request already names its PAN explicitly
 * (customer_pan_id / pan_type), so nothing server-side depends on it. It is
 * stored per browser, keyed to the customer's id so a re-login in the same
 * browser cannot inherit the previous customer's PAN, and wiped on logout via
 * tokenStore.clear() (see lib/api.ts). Absent or unknown means the unfiltered
 * "All PANs" view, which is the default.
 *
 * Pages also declare a PanConstraint (see `usePanConstraint`) saying which PANs
 * they permit: starting a service restricts the switcher to the PAN types that
 * service is offered to, and an open request locks it to its own PAN.
 */
import * as React from 'react';
import { useAuth } from '@finmin/auth';
import { useProfile } from '@/api/profile';
import { PAN_VIEW_STORAGE_KEY } from '@/lib/api';
import type { CustomerPAN, PanConstraint } from '@/api/customer-pans';

/** A specific PAN's id, or `'all'` for the unfiltered view. */
export type PanViewSelection = string | 'all';

interface PanViewContextValue {
  selection: PanViewSelection;
  setSelection: (selection: PanViewSelection) => void;
  pans: CustomerPAN[];
  /** The CustomerPAN the current selection refers to, or undefined for 'all'. */
  selectedPan: CustomerPAN | undefined;
  /** What the current page allows the switcher to select. */
  constraint: PanConstraint;
  setConstraint: (constraint: PanConstraint) => void;
}

const PanViewContext = React.createContext<PanViewContextValue | null>(null);

const NO_CONSTRAINT: PanConstraint = { kind: 'none' };
const isBrowser = typeof window !== 'undefined';

/** Stored as {uid, panId} so another customer's choice is never reused. */
function readStored(userId: string | null): string | null {
  if (!isBrowser || !userId) return null;
  try {
    const raw = window.localStorage.getItem(PAN_VIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { uid?: string; panId?: string };
    if (parsed?.uid !== userId || typeof parsed?.panId !== 'string') return null;
    return parsed.panId;
  } catch {
    return null;
  }
}

function writeStored(userId: string | null, panId: string | null) {
  if (!isBrowser || !userId) return;
  try {
    if (panId === null) window.localStorage.removeItem(PAN_VIEW_STORAGE_KEY);
    else window.localStorage.setItem(
      PAN_VIEW_STORAGE_KEY,
      JSON.stringify({ uid: userId, panId }),
    );
  } catch {
    // A full or blocked store just means the choice does not persist.
  }
}

export function PanViewProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const userId = user?.id ?? null;

  const [constraint, setConstraint] = React.useState<PanConstraint>(NO_CONSTRAINT);
  const [stored, setStored] = React.useState<string | null>(null);

  const pans = React.useMemo(() => profile?.pans ?? [], [profile?.pans]);

  // Load once the customer is known, so a re-login in the same browser starts
  // from that customer's own choice rather than the previous one's.
  React.useEffect(() => {
    setStored(readStored(userId));
  }, [userId]);

  // A PAN deleted elsewhere leaves a stale id behind; fall back to the
  // unfiltered view rather than showing an empty selection.
  const selection: PanViewSelection =
    stored && pans.some((p) => p.id === stored) ? stored : 'all';

  const setSelection = React.useCallback(
    (next: PanViewSelection) => {
      const panId = next === 'all' ? null : next;
      writeStored(userId, panId);
      setStored(panId);
    },
    [userId],
  );

  const selectedPan = selection === 'all' ? undefined : pans.find((p) => p.id === selection);

  const value = React.useMemo(
    () => ({ selection, setSelection, pans, selectedPan, constraint, setConstraint }),
    [selection, setSelection, pans, selectedPan, constraint],
  );

  return <PanViewContext.Provider value={value}>{children}</PanViewContext.Provider>;
}

export function usePanView(): PanViewContextValue {
  const ctx = React.useContext(PanViewContext);
  if (!ctx) throw new Error('usePanView must be used within a PanViewProvider');
  return ctx;
}

/**
 * Declare which PANs this page allows, for as long as it is mounted.
 * Pass a memoized (or literal-stable) constraint; it is compared structurally.
 */
export function usePanConstraint(constraint: PanConstraint): void {
  const { setConstraint } = usePanView();
  const serialized = JSON.stringify(constraint);

  React.useEffect(() => {
    setConstraint(JSON.parse(serialized) as PanConstraint);
    return () => setConstraint(NO_CONSTRAINT);
  }, [serialized, setConstraint]);
}
